<template>
  <Teleport to="body">
    <Transition name="ram-palette">
      <div v-if="open" class="ram-palette__backdrop" @click="open = false">
        <div class="ram-palette" role="dialog" aria-modal="true" @click.stop>
          <input
            ref="inputEl"
            v-model="query"
            type="text"
            class="ram-palette__input"
            placeholder="Search anything…  (⌘K to toggle)"
            aria-label="Command palette search"
          />
          <ul v-if="filtered.length" class="ram-palette__list">
            <li
              v-for="item in filtered"
              :key="item.id"
              class="ram-palette__item"
              tabindex="0"
              @click="trigger(item)"
              @keydown.enter="trigger(item)"
            >
              <span class="ram-palette__label">{{ item.label }}</span>
              <span v-if="item.hint" class="ram-palette__hint">{{ item.hint }}</span>
            </li>
          </ul>
          <p v-else class="ram-palette__empty">
            No matches yet. Phase 0 wires the shell — search across job
            numbers, parties, customers and vehicles lands in later phases.
          </p>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { useCommandPalette, type PaletteItem } from "../composables/useCommandPalette";

const { open, items } = useCommandPalette();

const query = ref("");
const inputEl = ref<HTMLInputElement | null>(null);

watch(open, async (isOpen) => {
  if (isOpen) {
    query.value = "";
    await nextTick();
    inputEl.value?.focus();
  }
});

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase();
  if (!q) return items.value.slice(0, 8);
  return items.value.filter((i) => i.label.toLowerCase().includes(q)).slice(0, 12);
});

function trigger(item: PaletteItem) {
  item.perform();
  open.value = false;
}
</script>

<style scoped>
.ram-palette__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  z-index: var(--ram-z-modal);
  display: flex;
  justify-content: center;
  padding-top: 12vh;
  backdrop-filter: blur(6px);
}
.ram-palette {
  width: min(640px, 92vw);
  background: var(--ram-color-surface-raised);
  color: var(--ram-color-text-primary);
  border: 1px solid var(--ram-color-border);
  border-radius: var(--ram-radius-lg);
  box-shadow: var(--ram-shadow-lg);
  overflow: hidden;
}
.ram-palette__input {
  width: 100%;
  padding: var(--ram-space-4) var(--ram-space-5);
  font-size: var(--ram-text-md);
  background: transparent;
  color: inherit;
  border: none;
  border-bottom: 1px solid var(--ram-color-border);
  outline: none;
}
.ram-palette__list {
  margin: 0;
  padding: var(--ram-space-2) 0;
  list-style: none;
  max-height: 50vh;
  overflow-y: auto;
}
.ram-palette__item {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: var(--ram-space-4);
  padding: var(--ram-space-3) var(--ram-space-5);
  cursor: pointer;
  transition: background var(--ram-duration-fast) var(--ram-ease-out);
}
.ram-palette__item:hover,
.ram-palette__item:focus {
  background: var(--ram-color-surface-muted);
  outline: none;
}
.ram-palette__label { font-weight: 500; }
.ram-palette__hint {
  color: var(--ram-color-text-muted);
  font-size: var(--ram-text-xs);
}
.ram-palette__empty {
  margin: 0;
  padding: var(--ram-space-6);
  color: var(--ram-color-text-secondary);
  font-size: var(--ram-text-sm);
  text-align: center;
}
.ram-palette-enter-active,
.ram-palette-leave-active {
  transition: opacity var(--ram-duration-base) var(--ram-ease-out);
}
.ram-palette-enter-from,
.ram-palette-leave-to { opacity: 0; }
.ram-palette-enter-active .ram-palette,
.ram-palette-leave-active .ram-palette {
  transition: transform var(--ram-duration-base) var(--ram-ease-out);
}
.ram-palette-enter-from .ram-palette,
.ram-palette-leave-to .ram-palette {
  transform: translateY(-8px) scale(0.98);
}
</style>
